/**
 * FontValidationSystem - Font resolution and validation for canvas rendering
 *
 * This module handles font resolver warming checks, font UID resolution,
 * and font availability validation before canvas rendering.
 */

import { FontResolver } from '../../../wordprocessing/services/FontResolver';

export interface FontValidationResult {
  resolvedFontUid: string;
  isFontResolverWarmed: boolean;
  fontResolverAvailable: boolean;
  cacheStats: any;
}

export interface FontValidationDeps {
  fontResolver: FontResolver | null;
  fontFamily: string;
  fontWeight: number;
  fontStyle: string;
  debugLog: (...args: any[]) => void;
}

/**
 * Validates font resolver state and resolves font UID for rendering
 *
 * @param deps - Font validation dependencies
 * @returns Font validation result or null if validation fails
 */
export function validateAndResolveFont({
  fontResolver,
  fontFamily,
  fontWeight,
  fontStyle,
  debugLog
}: FontValidationDeps): FontValidationResult | null {
  // Ensure font resolver is warmed (should already be warmed by DocumentOrchestrator)
  const cacheStats = fontResolver?.getCacheStats();
  const isFontResolverWarmed = cacheStats?.isWarmed ?? false;

  if (!isFontResolverWarmed) {
    console.warn('🎨 [CanvasV3] ⚠️ Font resolver not warmed, this should not happen!');
    debugLog('🎨 [CanvasV3] Cache stats:', cacheStats);
  }

  // Resolve font UID (single call - should work if fonts are warmed)
  const resolvedFontUid = fontResolver?.resolveFontUidSync(fontFamily, fontWeight, fontStyle) || '';

  debugLog('🎨 [CanvasV3] Font resolution:', {
    fontFamily,
    fontWeight,
    fontStyle,
    resolvedFontUid,
    fontResolverAvailable: !!fontResolver,
    cacheStats
  });

  if (!resolvedFontUid) {
    console.error('🎨 [CanvasV3] ❌ CRITICAL: Font resolution failed! Cannot proceed with rendering.');
    console.error('🎨 [CanvasV3] Font resolver cache contents:', fontResolver?.getCachedFamilies());
    return null;
  }

  return {
    resolvedFontUid,
    isFontResolverWarmed,
    fontResolverAvailable: !!fontResolver,
    cacheStats
  };
}
